<template>
    <div
        id="rphq-embed-main"
        class="overflow-hidden p-1"
    >
        <prime-button
            v-if="showBackButton"
            class="-mb-5 !px-2 !py-1"
            icon="pi pi-chevron-left"
            :label="t('back')"
            @click="router.go(-1)"
        />

        <slot />

        <powered-by-footer class="mt-6" />
    </div>
</template>

<script setup lang="ts">
import { provideViewer, useViewer } from '~/composables/use-login'
import { computed, onBeforeUnmount, ref } from 'vue'
import { useRpQuery } from '~/composables/graphql'
import { useQuery } from '@tanstack/vue-query'
import { graphql } from '~/resources/graphql'
import { useRequestURL, useRoute } from '#app'
import { useHead } from 'unhead'
import { provideEmbedParams } from '~/composables/use-embed-params'
import { useRouter } from 'vue-router'
import PrimeButton from 'primevue/button'
import PoweredByFooter from '~/components/PoweredByFooter.vue'
import { provideAnalyticsOrganizationFragment } from '~/composables/use-analytics-script'
import { provideLocalizationOrganizationFragment } from '~/composables/use-localization'
import { provideLoginModalOrganizationFragment } from '~/composables/use-login-modal-organization'
import {
    type ActiveFacility,
    provideActiveFacility
} from '~/composables/use-active-facility'
import { provideDefaultOrgCountry } from '~/components/Input/phone-number-countries'
import { useI18n } from 'vue-i18n'

const { t } = useI18n({ useScope: 'global' })

useHead({
    script: {
        type: 'text/javascript',
        tagPosition: 'bodyOpen',
        innerHTML:
            'setInterval(() => {\n' +
            "  const rphqEmbedMain = document.getElementById('rphq-embed-main');\n" +
            '  if (rphqEmbedMain !== null) {\n' +
            "    parent.postMessage('RPHQ::1::' + JSON.stringify({" +
            "      key: 'iframe-resize',\n" +
            '      height: rphqEmbedMain.scrollHeight\n' +
            "    }), '*');\n" +
            '  }\n' +
            '}, 50);'
    }
})

provideEmbedParams()
const router = useRouter()
const route = useRoute()

const showBackButton = computed(() => route.query.origin !== route.params.slug)

const url = useRequestURL()
const query = useRpQuery({ orgLevel: true })
const { data, suspense } = useQuery({
    queryKey: ['OrgEmbedLayoutQuery', url.hostname],
    queryFn: () =>
        query(
            graphql(/* GraphQL */ `
                query OrgEmbedLayoutQuery {
                    organization {
                        branding {
                            styleString
                            headLinks
                        }
                        country
                        ...UseAnalyticsScriptOrganizationFragment
                        ...UseLocalizationOrganizationFragment
                        ...UseLoginModalOrganizationFragment
                    }
                }
            `)
        )
})

const organization = computed(() => data.value?.organization)

await Promise.all([suspense(), provideViewer()])

if (organization.value === undefined) {
    throw new Error(t('layout.pageNotFound'))
}

const viewer = useViewer()
if (viewer.value !== undefined) {
    const homeFacility = ref<ActiveFacility>(viewer.value.homeFacility)
    provideActiveFacility(homeFacility)
}

const unmount = ref(false)
const headStyle = computed(() => {
    if (unmount.value) {
        return []
    }

    return organization.value?.branding.styleString
})

const headLinks = computed(() => {
    if (!organization.value) {
        return []
    }

    return organization.value.branding.headLinks
})

useHead({
    style: headStyle,
    link: headLinks
})

onBeforeUnmount(() => {
    // Clear styles when leaving the component
    unmount.value = true
})

provideAnalyticsOrganizationFragment(organization.value)
provideLocalizationOrganizationFragment(organization.value)
provideLoginModalOrganizationFragment(organization.value)
provideDefaultOrgCountry(organization.value.country)
</script>

<style>
#__nuxt {
    font-size: 13px;
}
</style>
